import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import MainLayout from '../../components/layout/MainLayout';
import Avatar from '../../components/common/Avatar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Lock,
  Save,
  Loader2,
  Camera,
  Shield,
  Bell,
  CreditCard,
  Building2,
  Calendar,
  CheckCircle2
} from 'lucide-react';
import { cn } from '../../lib/utils';
import api from '../../lib/axios';
import { toast } from 'sonner';

const ProfilePage = () => {
  const { user, refreshUser } = useAuth();
  const { theme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = React.useRef(null);

  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    address: '',
    bio: ''
  });

  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });

  const [notifSettings, setNotifSettings] = useState({
    email_notifications: true,
    project_updates: true,
    marketing: false
  });

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('L\'image ne doit pas dépasser 5 Mo');
      return;
    }

    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await api.post('/users/me/avatar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Photo de profil mise à jour !');
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du téléchargement');
    } finally {
      setUploadingAvatar(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  useEffect(() => {
    if (user) {
      setProfile({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        company: user.company || '',
        address: user.address || '',
        bio: user.bio || ''
      });
    }
  }, [user]);

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.put('/users/me', profile);
      toast.success('Profil mis à jour !');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwords.new !== passwords.confirm) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (passwords.new.length < 6) {
      toast.error('Le mot de passe doit faire au moins 6 caractères');
      return;
    }

    setSavingPassword(true);
    try {
      await api.put('/users/me/password', {
        current_password: passwords.current,
        new_password: passwords.new
      });
      toast.success('Mot de passe modifié !');
      setPasswords({ current: '', new: '', confirm: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally {
      setSavingPassword(false);
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      agency: 'bg-purple-100 text-purple-700',
      client: 'bg-blue-100 text-blue-700',
      team_member: 'bg-green-100 text-green-700'
    };
    const labels = { agency: 'Agence', client: 'Client', team_member: 'Équipe' };
    return <Badge className={styles[role] || 'bg-gray-100'}>{labels[role] || role}</Badge>;
  };

  // Build full avatar URL
  const getAvatarUrl = (avatarPath) => {
    if (!avatarPath) return null;
    if (avatarPath.startsWith('http')) return avatarPath;
    // For relative paths, prepend the backend URL
    const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    return `${backendUrl}${avatarPath}`;
  };

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-6 mb-8">
          <div className="relative">
            {user?.avatar_url ? (
              <img 
                src={getAvatarUrl(user.avatar_url)} 
                alt="Avatar" 
                className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg"
              />
            ) : (
              <Avatar name={`${user?.first_name} ${user?.last_name}`} size="xl" type={user?.role === 'agency' ? 'agency' : user?.role === 'client' ? 'client' : 'creative'} />
            )}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleAvatarChange}
              accept="image/*"
              className="hidden"
            />
            <button 
              onClick={handleAvatarClick}
              disabled={uploadingAvatar}
              className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-white border border-[#e2e8f0] flex items-center justify-center shadow-sm hover:bg-[#f8fafc] disabled:opacity-50"
            >
              {uploadingAvatar ? (
                <Loader2 className="w-4 h-4 text-[#64748b] animate-spin" />
              ) : (
                <Camera className="w-4 h-4 text-[#64748b]" />
              )}
            </button>
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
                {user?.first_name} {user?.last_name}
              </h1>
              {getRoleBadge(user?.role)}
            </div>
            <p className="text-[#64748b]">{user?.email}</p>
            <p className="text-sm text-[#94a3b8] flex items-center gap-1 mt-1">
              <Calendar className="w-3 h-3" />
              Membre depuis {new Date(user?.created_at || Date.now()).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
            </p>
          </div>
        </div>

          <Tabs defaultValue="profile" className="space-y-6">
            <TabsList>
              <TabsTrigger value="profile" className="flex items-center gap-2">
                <User className="w-4 h-4" />Profil
              </TabsTrigger>
              <TabsTrigger value="security" className="flex items-center gap-2">
                <Shield className="w-4 h-4" />Sécurité
              </TabsTrigger>
              <TabsTrigger value="notifications" className="flex items-center gap-2">
                <Bell className="w-4 h-4" />Notifications
              </TabsTrigger>
            </TabsList>

            {/* Profile Tab */}
            <TabsContent value="profile">
              <Card className="card">
                <CardHeader>
                  <CardTitle>Informations personnelles</CardTitle>
                  <CardDescription>Mettez à jour vos informations de profil</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Prénom</Label>
                      <Input value={profile.first_name} onChange={(e) => setProfile({ ...profile, first_name: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                      <Label>Nom</Label>
                      <Input value={profile.last_name} onChange={(e) => setProfile({ ...profile, last_name: e.target.value })} className="mt-1" />
                    </div>
                  </div>

                  <div>
                    <Label>Email</Label>
                    <Input type="email" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} className="mt-1" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Téléphone</Label>
                      <Input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} placeholder="+33 6 12 34 56 78" className="mt-1" />
                    </div>
                    <div>
                      <Label>Entreprise</Label>
                      <Input value={profile.company} onChange={(e) => setProfile({ ...profile, company: e.target.value })} className="mt-1" />
                    </div>
                  </div>

                  <div>
                    <Label>Adresse</Label>
                    <Input value={profile.address} onChange={(e) => setProfile({ ...profile, address: e.target.value })} placeholder="12 Rue de la Paix, 75001 Paris" className="mt-1" />
                  </div>

                  <div>
                    <Label>Bio</Label>
                    <Textarea value={profile.bio} onChange={(e) => setProfile({ ...profile, bio: e.target.value })} placeholder="Quelques mots sur vous..." className="mt-1" rows={3} />
                  </div>

                  <div className="flex justify-end">
                    <Button onClick={handleSaveProfile} disabled={savingProfile} style={{ backgroundColor: theme.colors?.accent }}>
                      {savingProfile ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Enregistrer
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Security Tab */}
            <TabsContent value="security">
              <Card className="card">
                <CardHeader>
                  <CardTitle>Changer le mot de passe</CardTitle>
                  <CardDescription>Assurez-vous d&apos;utiliser un mot de passe fort</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Mot de passe actuel</Label>
                    <Input type="password" value={passwords.current} onChange={(e) => setPasswords({ ...passwords, current: e.target.value })} className="mt-1" />
                  </div>
                  <div>
                    <Label>Nouveau mot de passe</Label>
                    <Input type="password" value={passwords.new} onChange={(e) => setPasswords({ ...passwords, new: e.target.value })} className="mt-1" />
                  </div>
                  <div>
                    <Label>Confirmer le mot de passe</Label>
                    <Input type="password" value={passwords.confirm} onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })} className="mt-1" />
                  </div>
                  <div className="flex justify-end">
                    <Button onClick={handleChangePassword} disabled={savingPassword} style={{ backgroundColor: theme.colors?.accent }}>
                      {savingPassword ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
                      Changer le mot de passe
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Notifications Tab */}
            <TabsContent value="notifications">
              <Card className="card">
                <CardHeader>
                  <CardTitle>Préférences de notifications</CardTitle>
                  <CardDescription>Gérez vos notifications email</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {[
                    { key: 'email_notifications', label: 'Notifications par email', desc: 'Recevoir les notifications importantes par email' },
                    { key: 'project_updates', label: 'Mises à jour projets', desc: 'Être notifié des changements sur vos projets' },
                    { key: 'marketing', label: 'Communications marketing', desc: 'Recevoir nos actualités et offres spéciales' }
                  ].map((item) => (
                    <div key={item.key} className="flex items-center justify-between p-4 rounded-xl border border-[#e2e8f0]">
                      <div>
                        <p className="font-medium text-[#1a365d]">{item.label}</p>
                        <p className="text-sm text-[#64748b]">{item.desc}</p>
                      </div>
                      <button
                        onClick={() => setNotifSettings({ ...notifSettings, [item.key]: !notifSettings[item.key] })}
                        className={cn("w-12 h-6 rounded-full transition-colors relative", notifSettings[item.key] ? "bg-[var(--color-accent)]" : "bg-[#e2e8f0]")}
                      >
                        <span className={cn("absolute top-1 w-4 h-4 rounded-full bg-white transition-transform", notifSettings[item.key] ? "translate-x-7" : "translate-x-1")} />
                      </button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
    </MainLayout>
  );
};

export default ProfilePage;